<script>

	import { onMount, tick } from 'svelte';
	import { fade } from "svelte/transition";
	import fadeScale from 'svelte-transitions-fade-scale';

	import Badge from "./Banner/Badge.svelte";
	import Highlight from "./Banner/Highlight.svelte";

	import { event, room, session, banner, banners, highlight } from "../lib/stores.js";
	import { getServerData } from '../lib/prelude.js';

	export let bannerHeight = 0;
	export let contentKey = null;

	let text = null;
	let fadeDuration = 0;

	let textElement;
	let textHeight;
	// let containerHeight;

	let font;
	let textColor;
	let bgColor;
	let bgOpacity;
	let fontWeight = 700;

	let fs = 0.04;

	let pollResults = null;
	let mounted = false;
	let badgeAspectRatio = 0;

	onMount(async () => {
		if ($highlight) {
			fs = 0.025;
			if ($highlight.t == 'poll') {
				await getPollResults();
			}
		} else {
			setText();
		}
		mounted = true;
		checkOverflow();
	});

	function setText() {
		let bnr = null;
		if ($banner) {
			for (const b of $banners) {
				if (b.ref == $banner) {
					bnr = b.text;
					break;
				}
			}
		}
		text = bnr;
		if (mounted) {
			fs = 0.04;
			checkOverflow();
		}
	}

	async function getPollResults() {
		const pollData = await getServerData('virtual/polls', {
			poll: $highlight.r
		});
		if (pollData.results) {
			pollResults = pollData.results;
		}
	}

	async function checkOverflow(iteration) {
		if (iteration == undefined) {
			iteration = 0;
		}
		if (mounted) {
			await tick();
			if ((!bannerHeight || !textHeight) && (iteration < 100)) {				
				setTimeout(() => {
					iteration++;
					checkOverflow(iteration);
				}, 10);
			}
			if (textHeight > (bannerHeight + 1)) { // Mux headless Chrome seems to have an off-by-one error here, so let's compensate
				fs = fs - 0.0005;
				if (fs > 0.005) {
					setTimeout(() => {
						checkOverflow();
					}, 10);
				}
			}
		}
	}

	function setStyles() {
		if ($event.setup.virtual && $event.setup.virtual.studio && $event.setup.virtual.studio.banners) {
			font = $event.setup.virtual.studio.banners.font;
			textColor = $event.theme.colors[$event.setup.virtual.studio.banners.textColor];
			bgColor = $event.theme.colors[$event.setup.virtual.studio.banners.bgColor];
			bgOpacity = parseInt($event.setup.virtual.studio.banners.bgOpacity) / 100;
			badgeAspectRatio = ($event.setup.virtual.studio.banners.badge && $event.setup.virtual.studio.banners.badge.aspectRatio) ? $event.setup.virtual.studio.banners.badge.aspectRatio : 0;
			if ($event.theme && $event.theme.fonts && $event.theme.fonts.headings && $event.theme.fonts.headings.name && (font == $event.theme.fonts.headings.name) && $event.theme.fonts.headings.weight) {
				fontWeight = $event.theme.fonts.headings.weight;
			}
		} else {
			font = 'Inter';
			textColor = 'var(--textColor)';
			bgColor = 'var(--panelColor)';
			bgOpacity = 0.9;
			badgeAspectRatio = 0;
		}
	}

	// $: if (fs) {
	// 	checkOverflow();
	// }

	$: if ($event) {
		setStyles();
	}

	$: if ($banners) {
		setText();
	}

</script>

<style>
	.banner {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		z-index: 3;
		padding: calc(var(--sw) * 0.03) calc(var(--sw) * 0.04);
		box-sizing: border-box;
		color: var(--bannerTextColor);
		font-size: calc(var(--sw) * var(--fs));
		overflow: hidden;
		line-height: 1.2;
		font-family: var(--bannerFont);
	}
	.banner.badged {
		padding-left: clamp(calc((var(--sw) * 0.06) + (var(--sw) * 0.07)), calc((var(--sw) * 0.06 / (var(--badgeAspectRatio) / 100)) + (var(--sw) * 0.07)), calc((var(--sw) * 0.15) + (var(--sw) * 0.07)));
/*		padding-left: calc(var(--sw) * 0.15);*/
/*		padding-left: calc(var(--sw) * 0.1 * (var(--badgeAspectRatio)) / 100);*/
/*		padding-left: calc(var(--sw) * 0.205);*/
	}
	.banner:before {
		content: '';
		position: absolute;
		inset: calc(var(--sw) * 0.018);
		border-radius: calc(var(--sw) * 0.005);
		background-color: var(--bannerBgColor);
		opacity: var(--bannerBgOpacity);
		z-index: 0;
		box-shadow: 0 0 calc(var(--sw) * 0.01) rgba(0, 0, 0, 0.3);
/*		border: calc(var(--sw) * 0.001) solid var(--bannerTextColor);*/
		transition: background-color 0.4s ease, opacity 0.4s ease;
	}
	.inner {
		position: relative;
		z-index: 2;
		max-height: calc(var(--sw) * 0.15);
	}
	.banner :global(p) {
		margin: 0;
		color: var(--bannerTextColor);
		transition: color 0.4s ease;
		white-space: pre-wrap;
		font-size: inherit;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}
	p {
		font-weight: var(--bannerFontWeight);
		min-height: calc(var(--sw) * 0.065);
	}
</style>

{#if (contentKey != null) && mounted}
	<div
		class="banner"
		class:badged={$banner && $event.setup.virtual && $event.setup.virtual.studio && $event.setup.virtual.studio.banners && $event.setup.virtual.studio.banners.badge}
		in:fadeScale={{ duration: 200, delay: 200, baseScale: 0.95 }}
		style="--bannerFont:{font};--bannerFontWeight:{fontWeight};--bannerTextColor:{textColor};--bannerBgColor:{bgColor};--bannerBgOpacity:{bgOpacity};--badgeAspectRatio:{badgeAspectRatio};--fs:{fs};"
	>
		<div class="inner" bind:clientHeight={bannerHeight}>
			<div
				class="text"
				bind:this={textElement}
				bind:clientHeight={textHeight}
			>
				{#if $banner && text}
					<p in:fade={{ duration: 200, delay: 400 }}>{text}</p>
				{:else if $highlight}
					<Highlight {pollResults} on:ready={checkOverflow}/>
				{/if}
			</div>
		</div>
		{#if $banner && $event.setup.virtual && $event.setup.virtual.studio && $event.setup.virtual.studio.banners && $event.setup.virtual.studio.banners.badge}
			{#key $event.setup.virtual.studio.banners.badge.filename}
				<Badge/>
			{/key}
		{/if}
	</div>
{/if}